import React, { useState, useRef, useEffect } from "react";
import { PieChart, Pie, Cell, Sector } from "recharts";
import { Card, Typography, Box, Stack, Popper } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { Global, css } from "@emotion/react";
import { isMobile } from "../../globalStyles/mobile";
import { useDarkMode } from "../context/darkModeContext";
import { formatDate } from "../../utils/formatDate";

export default function Delivery({
  data_obj = [],
  selectedDate,
  setSelectedDate,
  onDateChange,
  enableCalendar = false,
}) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const calendarRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const { colors } = useDarkMode();

  // Close calendar on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        calendarContainerRef.current &&
        !calendarContainerRef.current.contains(event.target) &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    }
    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  const COLORS = [
    { status: "Delivered", fill: "#057C82" },
    { status: "Undelivered", fill: "#AFDCDE" },
    { status: "In Transit", fill: "#3BBDC4" },
    { status: "Out for Delivery", fill: "#72D0CB" },
  ];

  // Custom label for Pie slices
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={14}
        fontWeight={700}
      >
        {data_obj[index]?.count || 0}
      </text>
    );
  };

  // Custom active shape for pie slice on hover
  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
      props;

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 5} // Make the active slice slightly larger
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          opacity={1}
        />
      </g>
    );
  };

  // Handlers for pie slice hover
  const onPieEnter = (data, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  return (
    <Card
      sx={{
        p: 2,
        borderRadius: 2,
        boxShadow: 0,
        width: "100%",
        minHeight: 350,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Global
        styles={css`
          .recharts-sector {
            outline: none !important;
            cursor: pointer !important; /* Change to pointer to indicate interactivity */
          }
          .recharts-wrapper {
            outline: none !important;
          }
        `}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="text.primary"
          sx={{ fontSize: isMobile() ? 12 : 14 }}
        >
          SHIPPED
        </Typography>
        {enableCalendar ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              background: colors.bgPaper,
              fontSize: 14,
              color: colors.textSecondary,
              gap: 1,
              cursor: "pointer",
            }}
            ref={calendarRef}
            onClick={() => setShowCalendar((prev) => !prev)}
          >
            <CalendarMonthOutlinedIcon
              sx={{ fontSize: 18, color: "#BDBDBD" }}
            />
            <span style={{ fontSize: 12, color: colors.textTertiary }}>
              {formatDate(selectedDate, "Select Date")}
            </span>
          </Box>
        ) : (
          <></>
        )}
        <Popper
          open={showCalendar}
          anchorEl={calendarRef.current}
          placement="bottom-start"
          disablePortal
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 4],
              },
            },
          ]}
          sx={{ zIndex: 20000 }}
        >
          <Box
            ref={calendarContainerRef}
            sx={{
              bgcolor: colors.bgPaper,
              boxShadow: 3,
              borderRadius: 1,
              p: 1,
              zIndex: 20000,
              width: 300,
              "& .react-calendar": {
                width: "100%",
              },
            }}
          >
            <Calendar
              value={selectedDate ? new Date(selectedDate) : new Date()}
              onChange={(e) => {
                setShowCalendar(false);
                if (onDateChange) onDateChange(e);
              }}
              maxDate={new Date()}
              className="custom-calendar"
              defaultActiveStartDate={selectedDate ?? new Date()}
            />
          </Box>
        </Popper>
      </Stack>
      <Box
        sx={{
          height: 200,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          // Remove the pointerEvents: none to enable hover effects
        }}
      >
        <PieChart width={180} height={180}>
          <Pie
            data={data_obj.map((x, i) => ({ name: x.status, value: x.count }))}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={80}
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            style={{ outline: "none" }}
            isAnimationActive={false}
            activeIndex={activeIndex !== null ? activeIndex : undefined}
            activeShape={renderActiveShape}
            onMouseEnter={onPieEnter}
            onMouseLeave={onPieLeave}
          >
            {data_obj.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  COLORS.find((x) => x.status === entry.status)?.fill ||
                  COLORS[index % COLORS.length].fill
                }
                style={{ outline: "none" }}
              />
            ))}
          </Pie>
        </PieChart>
      </Box>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        mt={1}
        // maxWidth={300}
        sx={{
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none", // For Firefox
          flexWrap: "wrap",
        }}
      >
        {data_obj.map((x, idx) => (
          <Stack
            key={x.status}
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              transition: "transform 0.2s ease",
              transform: activeIndex === idx ? "scale(1.05)" : "scale(1)",
              cursor: "pointer",
              textAlign: "center",
              // "&:hover": {
              //   transform: "scale(1.2)",
              // },
            }}
            onMouseEnter={() => setActiveIndex(idx)}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={0.5}
            >
              <Typography
                variant="caption"
                color={activeIndex === idx ? "text.primary" : "text.secondary"}
                sx={{
                  fontSize: activeIndex === idx ? 14 : 10,
                  fontWeight: activeIndex === idx ? 600 : 400,
                  transition: "all 0.2s",
                }}
              >
                {x.count || 0}
              </Typography>
              <Box alignItems={"center"}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    minWidth: 12,
                    minHeight: 12,
                    borderRadius: "50%",
                    backgroundColor:
                      COLORS.find((xx) => xx.status === x.status)?.fill ||
                      COLORS[idx % COLORS.length].fill,
                    border: "0px solid #e0e0e0",
                    display: "inline-block",
                    transition: "transform 0.2s",
                    transform: activeIndex === idx ? "scale(1.2)" : "scale(1)",
                    marginRight: 1,
                  }}
                />

                <Typography
                  variant="caption"
                  color={
                    activeIndex === idx ? "text.primary" : "text.secondary"
                  }
                  sx={{
                    fontSize: activeIndex === idx ? 12 : 10,
                    fontWeight: activeIndex === idx ? 600 : 400,
                    transition: "all 0.2s",
                  }}
                >
                  {x.status}
                </Typography>
              </Box>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}
